import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { 
  CorrectionType, 
  CorrectionInvoiceFormData, 
  CorrectionInvoiceItemFormData,
  CreateCorrectionInvoiceRequest,
  CreateCorrectionInvoiceItemRequest
} from '../types/correctionInvoice';
import { correctionInvoiceService } from '../services/correctionInvoiceService';
import { invoiceService } from '../services/invoiceService';
import { companyService } from '../services/companyService';
import { Invoice } from '../types/invoice';
import { Company, Currency } from '../types/company';
import { useTranslation } from '../hooks/useTranslation';
import { SearchableSelect } from './SearchableSelect';
import { Language, PDF_LANGUAGE_OPTIONS } from '../types/language';
import { useLanguage } from '../contexts/LanguageContext';

interface CorrectionInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: string;
  originalInvoiceId?: string;
}

const CorrectionInvoiceModal: React.FC<CorrectionInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  originalInvoiceId
}) => {
  const { t } = useTranslation();
  const { language: systemLanguage } = useLanguage();
  const [originalInvoice, setOriginalInvoice] = useState<Invoice | null>(null);
  const [availableInvoices, setAvailableInvoices] = useState<Invoice[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const { register, control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<CorrectionInvoiceFormData>({
    defaultValues: {
      originalInvoiceId: originalInvoiceId || '',
      correctionNumber: '',
      correctionDate: new Date().toISOString().split('T')[0],
      reason: '',
      correctionType: CorrectionType.CORRECTION,
      pdfLanguage: systemLanguage || Language.PL_ONLY,
      items: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedItems = watch('items');

  // Загружаем компанию для получения банковских счетов
  useEffect(() => {
    if (isOpen) {
      loadCompany();
    }
  }, [isOpen, companyId]);

  // Загружаем список доступных счетов при открытии модального окна
  useEffect(() => {
    if (isOpen && !originalInvoiceId) {
      loadAvailableInvoices();
    }
  }, [isOpen, originalInvoiceId]);

  // Загружаем оригинальный счет при открытии модального окна
  useEffect(() => {
    if (isOpen && originalInvoiceId) {
      loadOriginalInvoice();
    }
  }, [isOpen, originalInvoiceId]);

  // Показываем preview номера корректировки при изменении даты (без инкрементации счетчика)
  useEffect(() => {
    if (isOpen) {
      const currentNumber = watch('correctionNumber');
      // Если номер пустой или совпадает с предыдущим preview, обновляем preview
      if (!currentNumber) {
        previewCorrectionNumber();
      }
    }
  }, [isOpen, watch('correctionDate')]);

  const loadCompany = async () => {
    try {
      const companyData = await companyService.getCompany(companyId);
      setCompany(companyData);
    } catch (err) {
      console.error('Error loading company:', err);
    }
  };

  const loadAvailableInvoices = async () => {
    try {
      setLoadingInvoices(true);
      // Загружаем все счета компании
      const invoices = await invoiceService.getCompanyInvoices(companyId);
      
      // Сортируем по дате создания в обратном порядке (последние первыми)
      invoices.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      setAvailableInvoices(invoices);
    } catch (err) {
      setError(t('correctionInvoices.modal.loadError'));
      console.error('Error loading available invoices:', err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleInvoiceSelect = (invoiceId: string) => {
    const invoice = availableInvoices.find(inv => inv.id === invoiceId);
    if (invoice) {
      setOriginalInvoice(invoice);
      setValue('originalInvoiceId', invoice.id);
      
      // Устанавливаем банковский счет и валюту из выбранного инвойса
      if (invoice.currency) {
        setValue('currency', invoice.currency);
      }
      if (invoice.bankAccountId) {
        setValue('bankAccountId', invoice.bankAccountId);
      }
      if (invoice.exchangeRate) {
        setValue('exchangeRate', invoice.exchangeRate);
      }
      
      // Инициализируем позиции корректировки на основе выбранного счета
      initializeCorrectionItems(invoice);
    }
  };

  const initializeCorrectionItems = (invoice: Invoice) => {
    console.log('Initializing correction items for invoice:', invoice);
    console.log('Invoice items:', invoice.items);
    
    const correctionItems: CorrectionInvoiceItemFormData[] = invoice.items.map((item, index) => {
      // Вычисляем сумму НДС на основе ставки и общей суммы
      const taxRateValue = item.taxRate ? parseFloat(item.taxRate.replace('VAT_', '').replace('_', '.')) : 0;
      const taxAmount = item.totalPrice * (taxRateValue / 100);
      
      const correctionItem = {
        originalItemId: item.id,
        description: item.description,
        unitOfMeasure: item.unitOfMeasure,
        originalQuantity: item.quantity,
        originalUnitPrice: item.unitPrice,
        originalTotalPrice: item.totalPrice,
        originalTaxRate: taxRateValue,
        originalTaxAmount: taxAmount,
        correctedQuantity: item.quantity,
        correctedUnitPrice: item.unitPrice,
        correctedTotalPrice: item.totalPrice,
        correctedTaxRate: taxRateValue,
        correctedTaxAmount: taxAmount,
        sortOrder: index
      };
      
      console.log(`Correction item ${index}:`, correctionItem);
      return correctionItem;
    });
    
    console.log('All correction items:', correctionItems);
    setValue('items', correctionItems);
  };

  const loadOriginalInvoice = async () => {
    if (!originalInvoiceId) return;

    try {
      setLoading(true);
      const invoice = await invoiceService.getInvoice(companyId, originalInvoiceId);
      setOriginalInvoice(invoice);
      
      // Устанавливаем валюту и банковский счет из оригинального инвойса по умолчанию
      if (invoice.currency) {
        setValue('currency', invoice.currency);
      }
      if (invoice.bankAccountId) {
        setValue('bankAccountId', invoice.bankAccountId);
      }
      if (invoice.exchangeRate) {
        setValue('exchangeRate', invoice.exchangeRate);
      }
      
      // Инициализируем позиции корректировки на основе оригинального счета
      initializeCorrectionItems(invoice);
    } catch (err) {
      setError(t('correctionInvoices.modal.loadError'));
      console.error('Error loading original invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const previewCorrectionNumber = async () => {
    try {
      const correctionDate = watch('correctionDate');
      const response = await correctionInvoiceService.previewCorrectionNumber(companyId, correctionDate);
      setValue('correctionNumber', response.correctionNumber);
    } catch (err) {
      console.error('Error previewing correction number:', err);
    }
  };

  const generateCorrectionNumber = async () => {
    try {
      const correctionDate = watch('correctionDate');
      const response = await correctionInvoiceService.previewCorrectionNumber(companyId, correctionDate);
      setValue('correctionNumber', response.correctionNumber);
    } catch (err) {
      console.error('Error generating correction number:', err);
    }
  };

  const recalculateItem = (index: number) => {
    const item = watchedItems[index];
    if (item) {
      const correctedTotalPrice = item.correctedQuantity * item.correctedUnitPrice;
      const correctedTaxAmount = correctedTotalPrice * (item.correctedTaxRate / 100);
      
      setValue(`items.${index}.correctedTotalPrice`, correctedTotalPrice);
      setValue(`items.${index}.correctedTaxAmount`, correctedTaxAmount);
    }
  };

  const onSubmit = async (data: CorrectionInvoiceFormData) => {
    try {
      setSaving(true);
      setError('');
      
      console.log('Submitting correction invoice data:', data);
      console.log('Items data:', data.items);
      
      const request: CreateCorrectionInvoiceRequest = {
        originalInvoiceId: data.originalInvoiceId,
        correctionNumber: data.correctionNumber,
        correctionDate: data.correctionDate,
        reason: data.reason,
        correctionType: data.correctionType,
        currency: data.currency,
        bankAccountId: data.bankAccountId,
        exchangeRate: data.exchangeRate,
        pdfLanguage: data.pdfLanguage,
        items: data.items.map(item => ({
          originalItemId: item.originalItemId,
          description: item.description,
          unitOfMeasure: item.unitOfMeasure,
          originalQuantity: item.originalQuantity,
          originalUnitPrice: item.originalUnitPrice,
          originalTotalPrice: item.originalTotalPrice,
          originalTaxRate: item.originalTaxRate,
          originalTaxAmount: item.originalTaxAmount,
          correctedQuantity: item.correctedQuantity,
          correctedUnitPrice: item.correctedUnitPrice,
          correctedTotalPrice: item.correctedTotalPrice,
          correctedTaxRate: item.correctedTaxRate,
          correctedTaxAmount: item.correctedTaxAmount,
          sortOrder: item.sortOrder
        }))
      };

      await correctionInvoiceService.createCorrectionInvoice(companyId, request);
      onSuccess();
      onClose();
      reset();
    } catch (err) {
      setError(t('correctionInvoices.modal.createError'));
      console.error('Error creating correction invoice:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {t('correctionInvoices.modal.title')}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">{t('correctionInvoices.modal.loading')}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Основная информация */}
              <div className="space-y-4">
                {/* Скрытое поле для originalInvoiceId */}
                <input
                  type="hidden"
                  {...register('originalInvoiceId', { required: t('correctionInvoices.modal.originalInvoice') + ' ' + t('correctionInvoices.modal.required') })}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('correctionInvoices.modal.correctionNumber')} *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      {...register('correctionNumber', { required: t('correctionInvoices.modal.correctionNumber') + ' ' + t('correctionInvoices.modal.required') })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="KOR-001/09/2025"
                    />
                    <button
                      type="button"
                      onClick={generateCorrectionNumber}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title={t('invoiceNumbering.generateNewTooltip')}
                    >
                      🔄
                    </button>
                  </div>
                  {errors.correctionNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.correctionNumber.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('correctionInvoices.modal.correctionDate')} *
                  </label>
                  <input
                    type="date"
                    {...register('correctionDate', { required: t('correctionInvoices.modal.correctionDate') + ' ' + t('correctionInvoices.modal.requiredF') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.correctionDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.correctionDate.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('correctionInvoices.modal.correctionType')} *
                  </label>
                  <select
                    {...register('correctionType', { required: t('correctionInvoices.modal.correctionType') + ' ' + t('correctionInvoices.modal.required') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={CorrectionType.CORRECTION}>{t('correctionInvoices.types.CORRECTION')}</option>
                    <option value={CorrectionType.CANCELLATION}>{t('correctionInvoices.types.CANCELLATION')}</option>
                    <option value={CorrectionType.REPLACEMENT}>{t('correctionInvoices.types.REPLACEMENT')}</option>
                  </select>
                  {errors.correctionType && (
                    <p className="mt-1 text-sm text-red-600">{errors.correctionType.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.pdfLanguage')} *
                  </label>
                  <select
                    {...register('pdfLanguage', { required: true })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {PDF_LANGUAGE_OPTIONS.map(option => (
                      <option key={option.code} value={option.code}>
                        {option.nativeName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('correctionInvoices.modal.originalInvoice')} *
                  </label>
                  {originalInvoiceId ? (
                    // Если оригинальный счет уже выбран (из деталей счета), показываем его как неактивное поле
                    <input
                      type="text"
                      value={originalInvoice?.invoiceNumber || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  ) : (
                    // Если создаем корректировку из вкладки корректировок, показываем SearchableSelect
                    loadingInvoices ? (
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-center text-gray-500">
                        {t('correctionInvoices.modal.loading')}
                      </div>
                    ) : (
                      <SearchableSelect
                        options={availableInvoices.map((invoice) => ({
                          value: invoice.id,
                          label: invoice.invoiceNumber,
                          searchText: `${invoice.invoiceNumber} ${invoice.clientName || ''} ${invoice.totalAmount} ${invoice.currency} ${new Date(invoice.createdAt).toLocaleDateString()}`.trim()
                        }))}
                        value={watch('originalInvoiceId')}
                        onChange={(value) => handleInvoiceSelect(value)}
                        placeholder={t('correctionInvoices.modal.searchInvoice')}
                        required
                        error={errors.originalInvoiceId?.message}
                      />
                    )
                  )}
                  {errors.originalInvoiceId && (
                    <p className="mt-1 text-sm text-red-600">{errors.originalInvoiceId.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('correctionInvoices.modal.reason')} *
                </label>
                <textarea
                  {...register('reason', { required: t('correctionInvoices.modal.reason') + ' ' + t('correctionInvoices.modal.requiredF') })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('correctionInvoices.modal.reasonPlaceholder')}
                />
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('companies.bankAccount')}
                  </label>
                  <SearchableSelect
                    options={company?.bankAccounts?.map((account) => ({
                      value: account.id,
                      label: `${account.name} - ${account.accountNumber} (${account.currency})`,
                      searchText: `${account.name} ${account.accountNumber} ${account.bankName} ${account.currency}`.trim()
                    })) || []}
                    value={watch('bankAccountId') || ''}
                    onChange={(value) => {
                      setValue('bankAccountId', value);
                      // Автоматически устанавливаем валюту из выбранного счета
                      const selectedAccount = company?.bankAccounts?.find(acc => acc.id === value);
                      if (selectedAccount) {
                        setValue('currency', selectedAccount.currency);
                      }
                    }}
                    placeholder={t('common.selectBankAccount')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('invoices.currency')}
                  </label>
                  <select
                    {...register('currency')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={Currency.PLN}>{t('invoices.currencies.PLN') || 'PLN - Польский злотый'}</option>
                    <option value={Currency.USD}>{t('invoices.currencies.USD') || 'USD - Доллар США'}</option>
                    <option value={Currency.EUR}>{t('invoices.currencies.EUR') || 'EUR - Евро'}</option>
                    <option value={Currency.GBP}>{t('invoices.currencies.GBP') || 'GBP - Фунт стерлингов'}</option>
                    <option value={Currency.CAD}>{t('invoices.currencies.CAD') || 'CAD - Канадский доллар'}</option>
                    <option value={Currency.CNY}>{t('invoices.currencies.CNY') || 'CNY - Китайский юань'}</option>
                  </select>
                </div>
              </div>
              </div>

              {/* Позиции корректировки */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('correctionInvoices.modal.items')}</h3>
                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div key={field.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('invoices.description')}
                          </label>
                          <input
                            type="text"
                            {...register(`items.${index}.description` as const)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('correctionInvoices.modal.originalQuantity')}
                          </label>
                          <input
                            type="number"
                            step="0.001"
                            {...register(`items.${index}.originalQuantity` as const, { valueAsNumber: true })}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('correctionInvoices.modal.correctedQuantity')}
                          </label>
                          <input
                            type="number"
                            step="0.001"
                            {...register(`items.${index}.correctedQuantity` as const, { valueAsNumber: true })}
                            onChange={(e) => {
                              register(`items.${index}.correctedQuantity` as const).onChange(e);
                              setTimeout(() => recalculateItem(index), 0);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('correctionInvoices.modal.originalPrice')}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            {...register(`items.${index}.originalUnitPrice` as const, { valueAsNumber: true })}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('correctionInvoices.modal.correctedPrice')}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            {...register(`items.${index}.correctedUnitPrice` as const, { valueAsNumber: true })}
                            onChange={(e) => {
                              register(`items.${index}.correctedUnitPrice` as const).onChange(e);
                              setTimeout(() => recalculateItem(index), 0);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('correctionInvoices.modal.originalAmount')}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            {...register(`items.${index}.originalTotalPrice` as const, { valueAsNumber: true })}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('correctionInvoices.modal.correctedAmount')}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            {...register(`items.${index}.correctedTotalPrice` as const, { valueAsNumber: true })}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('correctionInvoices.modal.difference')}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={watchedItems[index]?.correctedTotalPrice - watchedItems[index]?.originalTotalPrice || 0}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                          />
                        </div>

                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            {t('correctionInvoices.modal.remove')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Кнопки */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('correctionInvoices.modal.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? t('correctionInvoices.modal.saving') : t('correctionInvoices.createCorrection')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorrectionInvoiceModal;
